// src/config/sentry.ts
// Sentry initialization for the React frontend
// Captures unhandled errors, rejected promises, and custom events

import * as Sentry from "@sentry/react";
import config from "@/config/env";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

/**
 * Initialize Sentry for the frontend.
 * Safe to call even if DSN is not configured — it becomes a no-op.
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.log("[Sentry] No DSN configured — skipping initialization");
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment based on hostname pattern
    environment: window.location.hostname.includes("uat") ? "uat" : "production",

    // Release tracking — matches VERSION file
    release: `qlt-pub-app@${import.meta.env.VITE_APP_VERSION || "0.4.0"}`,

    // Sample 100% of errors, 20% of transactions for performance
    sampleRate: 1.0,
    tracesSampleRate: 0.2,

    // Replay sampling — capture 10% of sessions, 100% of error sessions
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Track navigation for SPA routing
        instrumentNavigation: true
      }),
      // Session replay for debugging user-reported issues
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true, // Mask form inputs (PII protection)
        blockAllMedia: false
      })
    ],

    // Don't send errors in local development
    enabled: !import.meta.env.DEV,

    // Tag every event with the brand
    initialScope: {
      tags: {
        brand: config.hotel,
        codebase: "public"
      }
    },

    // Filter out noisy/irrelevant errors
    beforeSend(event) {
      // Skip browser extension errors
      if (
        event.exception?.values?.some(
          (e) => e.value?.includes("chrome-extension") || e.value?.includes("moz-extension")
        )
      ) {
        return null;
      }
      return event;
    }
  });

  // Set brand context for all events
  Sentry.setTag("hotel", config.hotel);
}

/**
 * Capture a custom error with optional context.
 * Use this in catch blocks throughout the app.
 */
export function captureError(
  error: unknown,
  context?: Record<string, string | number | boolean>
): void {
  if (error instanceof Error) {
    Sentry.captureException(error, {
      extra: context
    });
  } else {
    Sentry.captureMessage(String(error), {
      level: "error",
      extra: context
    });
  }
}

/**
 * Set user identity for error correlation.
 * Call after a booking is initiated with customer email.
 */
export function setSentryUser(email: string, brand?: string): void {
  Sentry.setUser({
    email,
    ...(brand && { brand })
  });
}

/**
 * Add breadcrumb for debugging context.
 * Use to trace user actions leading up to an error.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, string | number | boolean>
): void {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: "info"
  });
}

export { Sentry };
